import React, { useState } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import {
  DataTableWrapper,
  PageLayout,
  Button,
  PresenceModal,
} from '@adc/ui-morpheus'
import { useBidirectionalPresence } from '@adc/presence'
import { ROOT_CRUMB } from '../../constants/routing'
import { EVENTS } from '../../constants/tableTypes'
import {
  fetchTable,
  fetchTableAppend,
  addFilter,
  clearFilters,
  removeFilter,
  changeSortBy,
  abortTableValues,
  fetchTableBatches,
  resetTableValues,
  savedSearchActions,
} from '../../actions/events'
import { apiToUiDataRowsTranslation } from '../../translations/events'
import {
  columnSelector,
  getFilterClauses,
} from '../../reducers/events/columnDefinitions'
import eventsBulkUpdateModule from '../../components/eventsBulkUpdateModule'
import { mapDispatchToSavedSearchActionsWithLimit } from '../../util/savedSearchLimitCheck'

const TITLE = 'Events'

const EventsPage = props => {
  const tableProps = {
    columns: props.columns,
    filterClauses: props.filterClauses,
    addFilter: props.addFilter,
    removeFilter: props.removeFilter,
    clearFilters: props.clearFilters,
    changeSortBy: props.changeSortBy,
    fetchDataRows: props.fetchDataRows,
    fetchDataRowsAppend: props.fetchDataRowsAppend,
    isLoading: props.isLoading,
    errorInfo: props.errorInfo,
    totalCount: props.totalCount,
    rowData: props.rowData,
    downloadData: props.downloadData,
    downloadActions: props.downloadActions,
    savedSearchesActions: props.savedSearchesActions,
    savedSearches: props.savedSearches,
    userAuthorities: props.userAuthorities,
  }

  const [context] = useState({ action: 'viewing' })
  const presence = useBidirectionalPresence('morpheus.events.all', context)

  const buttons = [
    <PresenceModal key='presence' {...presence} />,
    <Link
      to='/schedule/events/create'
      key='create'
      data-elm-id='create_new_button'
    >
      <Button role='primary' size='small' style={{ whiteSpace: 'nowrap' }}>
        Create New
      </Button>
    </Link>,
  ]

  const bulkUpdateProps = {
    entityName: TITLE,
    bulkProcessingModules: [eventsBulkUpdateModule],
  }

  return (
    <PageLayout title={TITLE} crumbs={[ROOT_CRUMB, TITLE]} buttons={buttons}>
      <DataTableWrapper
        {...props.config}
        {...tableProps}
        {...bulkUpdateProps}
      />
    </PageLayout>
  )
}

EventsPage.propTypes = {
  config: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  filterClauses: PropTypes.array.isRequired,
  addFilter: PropTypes.func.isRequired,
  removeFilter: PropTypes.func.isRequired,
  clearFilters: PropTypes.func.isRequired,
  changeSortBy: PropTypes.func.isRequired,
  fetchDataRows: PropTypes.func.isRequired,
  fetchDataRowsAppend: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  errorInfo: PropTypes.object,
  rowData: PropTypes.array.isRequired,
  totalCount: PropTypes.number,
  downloadData: PropTypes.object.isRequired,
  downloadActions: PropTypes.shape({
    fetchData: PropTypes.func.isRequired,
    abort: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
  }),
  savedSearches: PropTypes.object,
  savedSearchesActions: PropTypes.object,
  userAuthorities: PropTypes.array,
}

const getTotalCount = state => state.total_count

const mapStateToProps = store => {
  const { content, isLoading, errorInfo } = store[EVENTS].data
  const downloadDataRaw = store[EVENTS].downloadData
  const downloadContent = downloadDataRaw.content
  const downloadData = {
    content: downloadContent ? apiToUiDataRowsTranslation(downloadContent) : [],
    totalCount: downloadDataRaw.total_count,
    totalRetrieved: downloadDataRaw.total_elements,
    errorInfo: downloadDataRaw.errorInfo,
    isBatchSuccessful: downloadDataRaw.isBatchSuccessful,
  }
  const config = store[EVENTS].tableInfo.config
  return {
    config,
    isLoading: !!isLoading,
    columns: columnSelector(store[EVENTS].tableInfo),
    filterClauses: getFilterClauses(store[EVENTS].tableInfo),
    totalCount: getTotalCount(store[EVENTS].data),
    rowData: apiToUiDataRowsTranslation(content),
    errorInfo,
    downloadData,
    savedSearches: store[EVENTS].savedSearches,
    userAuthorities: _.get(store, 'user.authorities', []),
  }
}

const mapDispatchToProps = dispatch => ({
  fetchDataRows: fetchOptions => dispatch(fetchTable(fetchOptions)),
  fetchDataRowsAppend: fetchOptions => dispatch(fetchTableAppend(fetchOptions)),
  addFilter: filterClause => dispatch(addFilter(filterClause)),
  removeFilter: filterClause => dispatch(removeFilter(filterClause)),
  clearFilters: _ => dispatch(clearFilters()),
  changeSortBy: sortValues => dispatch(changeSortBy(sortValues)),
  downloadActions: {
    fetchData: () => dispatch(fetchTableBatches()),
    abort: () => dispatch(abortTableValues(EVENTS, true)),
    reset: () => dispatch(resetTableValues(EVENTS, true)),
  },
  savedSearchesActions: mapDispatchToSavedSearchActionsWithLimit(
    dispatch,
    savedSearchActions,
    EVENTS
  ),
})

export default connect(mapStateToProps, mapDispatchToProps)(EventsPage)
